import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AnimatedKpiCard from '../../components/common/AnimatedKpiCard';
import ChartCard from '../../components/charts/ChartCard';
import { BarChart, DoughnutChart, LineChart, EmptyChart } from '../../components/charts/AnalyticsCharts';
import { AnalyticsToolbar, AnalyticsSettingsModal, AnalyticsInfoModal, useAnalyticsControls } from './AnalyticsControls';

const fmtINR = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(Number(n) || 0);
const ANALYTICS_PRESETS = [
  { value: '30d', label: '30 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
];
const MAX_COMPARISON_COURSES = 8;

const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (value) => {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
};

const shiftDate = (value, amount, unit) => {
  const d = new Date(value);
  if (unit === 'month') d.setMonth(d.getMonth() + amount);
  else if (unit === 'year') d.setFullYear(d.getFullYear() + amount);
  else d.setDate(d.getDate() + amount);
  return d;
};

const buildPreviousQueryParams = (queryParams = {}) => {
  const preset = String(queryParams.preset || '12m');
  const groupBy = queryParams.groupBy || 'month';

  if (preset === 'today') {
    const today = startOfDay(new Date());
    const previous = shiftDate(today, -1, 'day');
    return { preset: 'custom', groupBy, from: formatDate(previous), to: formatDate(previous) };
  }

  if (preset === '7d' || preset === '30d') {
    const days = preset === '7d' ? 7 : 30;
    const currentEnd = endOfDay(new Date());
    const currentStart = startOfDay(shiftDate(currentEnd, -(days - 1), 'day'));
    const previousEnd = endOfDay(shiftDate(currentStart, -1, 'day'));
    const previousStart = startOfDay(shiftDate(previousEnd, -(days - 1), 'day'));
    return { preset: 'custom', groupBy, from: formatDate(previousStart), to: formatDate(previousEnd) };
  }

  if (preset === 'month') {
    const now = new Date();
    const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    return { preset: 'custom', groupBy, from: formatDate(prevMonthStart), to: formatDate(prevMonthEnd) };
  }

  if (preset === 'year') {
    const now = new Date();
    return {
      preset: 'custom',
      groupBy,
      from: `${now.getFullYear() - 1}-01-01`,
      to: `${now.getFullYear() - 1}-12-31`,
    };
  }

  if (preset === '12m') {
    const now = new Date();
    const currentStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const previousStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 12, 1);
    const previousEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
    return { preset: 'custom', groupBy, from: formatDate(previousStart), to: formatDate(previousEnd) };
  }

  if (queryParams.from || queryParams.to) {
    const currentFrom = startOfDay(queryParams.from || queryParams.to || new Date());
    const currentTo = endOfDay(queryParams.to || queryParams.from || new Date());
    const diffMs = currentTo.getTime() - currentFrom.getTime();
    const previousTo = endOfDay(shiftDate(currentFrom, -1, 'day'));
    const previousFrom = startOfDay(new Date(previousTo.getTime() - diffMs));
    return { preset: 'custom', groupBy, from: formatDate(previousFrom), to: formatDate(previousTo) };
  }

  return { preset: '30d', groupBy };
};

const buildComparisonMeta = (currentMeta = {}, previousMeta = {}) => ({
  presentLabel: currentMeta.periodLabel || 'Present period',
  previousLabel: previousMeta.periodLabel || 'Previous period',
});

export default function BatchesAnalyticsV2() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [showCoursePicker, setShowCoursePicker] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const controls = useAnalyticsControls({
    storageKey: 'batches',
    chartKeys: ['strength', 'status', 'attendance', 'metrics', 'courseTrend'],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const previousParams = buildPreviousQueryParams(controls.queryParams);
      const [currentRes, previousRes] = await Promise.all([
        api.get('/training/analytics/batches', { params: controls.queryParams }),
        api.get('/training/analytics/batches', { params: previousParams }),
      ]);
      setData(currentRes.data?.data || {});
      setPreviousData(previousRes.data?.data || {});
    } catch (_) {
      setData({});
      setPreviousData({});
    } finally {
      setLoading(false);
    }
  }, [controls.queryParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/training/courses', { params: { status: 'active', limit: 500 } })
      .then((r) => {
        const rows = Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data?.records) ? r.data.records : [];
        setAllCourses(rows);
      })
      .catch(() => setAllCourses([]));
  }, []);

  const currentData = data || {};
  const previousAnalytics = previousData || {};
  const kpis = currentData.kpis || {};
  const strength = useMemo(() => currentData.strength || [], [currentData.strength]);
  const previousStrength = useMemo(() => previousAnalytics.strength || [], [previousAnalytics.strength]);
  const courseSeries = useMemo(() => currentData.courseTrendSeries || [], [currentData.courseTrendSeries]);
  const previousCourseSeries = useMemo(() => previousAnalytics.courseTrendSeries || [], [previousAnalytics.courseTrendSeries]);
  const statusSlices = currentData.statusSlices || [];
  const attendance = currentData.attendanceTrend || [];
  const meta = currentData.meta || {};
  const previousMeta = previousAnalytics.meta || {};
  const visible = controls.state.chartVisibility || {};
  const comparisonMeta = buildComparisonMeta(meta, previousMeta);
  const chartOptions = [
    { key: 'strength', label: 'Batch Strength', help: 'Top batches by student count' },
    { key: 'status', label: 'Batch Status', help: 'Upcoming, ongoing, completed, cancelled' },
    { key: 'attendance', label: 'Attendance Trend', help: 'Attendance percentage by selected grouping' },
    { key: 'metrics', label: 'Additional Metrics', help: 'Students and revenue summary' },
    { key: 'courseTrend', label: 'Course Line Comparison', help: 'Toggle individual course lines' },
  ];

  const courseOptions = useMemo(() => {
    const map = new Map();
    allCourses.forEach((item) => {
      const key = String(item._id || item.courseId || '');
      if (!key) return;
      if (!map.has(key)) map.set(key, { value: key, label: item.courseName || item.title || 'Course' });
    });
    [...courseSeries, ...previousCourseSeries].forEach((item) => {
      const key = String(item.courseId || item.courseName || '');
      if (!key || map.has(key)) return;
      map.set(key, { value: key, label: item.courseName || 'Course' });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [allCourses, courseSeries, previousCourseSeries]);

  useEffect(() => {
    setSelectedCourses((current) => {
      const valid = current.filter((value) => courseOptions.some((option) => option.value === value));
      if (valid.length) return valid;
      return courseOptions.slice(0, Math.min(4, courseOptions.length)).map((option) => option.value);
    });
  }, [courseOptions]);

  const activeCourseIds = useMemo(() => {
    const ids = [
      ...courseSeries.filter((item) => Number(item.total || 0) > 0).map((item) => String(item.courseId || item.courseName || '')),
      ...previousCourseSeries.filter((item) => Number(item.total || 0) > 0).map((item) => String(item.courseId || item.courseName || '')),
    ].filter(Boolean);
    return Array.from(new Set(ids));
  }, [courseSeries, previousCourseSeries]);

  useEffect(() => {
    if (!activeCourseIds.length) return;
    const activeSelected = selectedCourses.filter((value) => activeCourseIds.includes(value));
    if (activeSelected.length) return;
    const next = activeCourseIds.slice(0, Math.min(4, activeCourseIds.length));
    const same = next.length === selectedCourses.length && next.every((value, index) => value === selectedCourses[index]);
    if (!same) setSelectedCourses(next);
  }, [activeCourseIds, selectedCourses]);

  const selectedComparison = useMemo(() => {
    const currentMap = new Map(courseSeries.map((item) => [String(item.courseId || item.courseName || ''), item]));
    const previousMap = new Map(previousCourseSeries.map((item) => [String(item.courseId || item.courseName || ''), item]));
    const optionMap = new Map(courseOptions.map((option) => [option.value, option]));
    return selectedCourses.map((value) => {
      const present = currentMap.get(value);
      const previous = previousMap.get(value);
      const option = optionMap.get(value);
      return {
        key: value,
        courseName: option?.label || present?.courseName || previous?.courseName || 'Course',
        present: Number(present?.total || 0),
        previous: Number(previous?.total || 0),
      };
    }).filter((item) => item.courseName);
  }, [selectedCourses, courseSeries, previousCourseSeries, courseOptions]);

  const presentLabel = comparisonMeta.presentLabel;
  const previousLabel = comparisonMeta.previousLabel;
  const chartComparison = selectedComparison.slice(0, MAX_COMPARISON_COURSES);
  const comparisonOverflow = selectedComparison.length > MAX_COMPARISON_COURSES;
  const comparisonRows = [
    { period: 'Present', label: presentLabel, values: Object.fromEntries(selectedComparison.map((item) => [item.key, item.present])) },
    { period: 'Previous', label: previousLabel, values: Object.fromEntries(selectedComparison.map((item) => [item.key, item.previous])) },
  ];

  if (loading && !data) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div className="batches-analytics">
      <AnalyticsToolbar title="Batch Analytics" subtitle="Review batch strength, attendance, and status with custom dates." state={controls.state} meta={meta} presets={ANALYTICS_PRESETS} onPresetChange={controls.setPreset} onGroupByChange={controls.setGroupBy} onFromChange={controls.setFrom} onToChange={controls.setTo} onOpenSettings={() => setShowSettings(true)} onOpenInfo={() => setShowInfo(true)} />

      <div className="row g-3 mb-4">
        {visible.courseTrend !== false && (
          <div className="col-12">
            <ChartCard title="Present vs Previous Course Comparison" subtitle={`${presentLabel} compared with ${previousLabel}`} icon="bi bi-arrow-left-right" iconColor="#3b82f6" onClick={() => navigate('/batches')}>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <span className="badge text-bg-primary">{presentLabel}</span>
                    <span className="badge text-bg-light border">{previousLabel}</span>
                  </div>
                  <div className="w-100 position-relative">
                    <label className="form-label form-label-sm mb-2 fw-semibold">Courses for comparison</label>
                    <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between" style={{ minWidth: 320 }} onClick={() => setShowCoursePicker((current) => !current)}>
                      <span>{selectedCourses.length ? `${selectedCourses.length} course${selectedCourses.length > 1 ? 's' : ''} selected` : 'Select courses'}</span>
                      <i className={`bi ${showCoursePicker ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                    </button>
                    {showCoursePicker && (
                      <div className="position-absolute mt-2 p-3 border rounded-3 bg-white shadow-sm" style={{ zIndex: 20, minWidth: 320, maxWidth: 520, maxHeight: 280, overflowY: 'auto' }}>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => setSelectedCourses(courseOptions.slice(0, 6).map((option) => option.value))}>Top 6</button>
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => setSelectedCourses(courseOptions.map((option) => option.value))}>Select all</button>
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => setSelectedCourses([])}>Clear</button>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {courseOptions.map((option) => (
                            <label key={option.value} className="d-flex align-items-center gap-2 small">
                              <input type="checkbox" className="form-check-input mt-0" checked={selectedCourses.includes(option.value)} onChange={(e) => {
                                setSelectedCourses((current) => e.target.checked ? [...current, option.value] : current.filter((value) => value !== option.value));
                              }} />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {selectedCourses.map((value) => {
                        const option = courseOptions.find((entry) => entry.value === value);
                        return option ? <span key={value} className="badge rounded-pill text-bg-light border text-dark">{option.label}</span> : null;
                      })}
                    </div>
                  </div>
                </div>
                {selectedComparison.length ? (
                  <>
                    {comparisonOverflow && (
                      <div className="alert alert-warning py-2 px-3 mb-0">
                        Select up to {MAX_COMPARISON_COURSES} courses for chart view. The table below still shows all selected courses.
                      </div>
                    )}
                    <div className="row g-3">
                      <div className="col-12">
                        <ChartCard title="Present Trend" subtitle={presentLabel} icon="bi bi-graph-up-arrow" iconColor="#3b82f6" onClick={() => navigate('/batches')}>
                          <LineChart height={260} labels={chartComparison.map((item) => item.courseName.length > 22 ? `${item.courseName.slice(0, 21)}...` : item.courseName)} datasets={[{ label: 'Present', data: chartComparison.map((item) => item.present), color: '#3b82f6', fill: true, pointRadius: 4 }]} themeColor="#3b82f6" tooltipTitle={(index) => chartComparison[index]?.courseName || ''} yMin={0} />
                        </ChartCard>
                      </div>
                      <div className="col-12">
                        <ChartCard title="Previous Trend" subtitle={previousLabel} icon="bi bi-graph-up" iconColor="#94a3b8" onClick={() => navigate('/batches')}>
                          <LineChart height={260} labels={chartComparison.map((item) => item.courseName.length > 22 ? `${item.courseName.slice(0, 21)}...` : item.courseName)} datasets={[{ label: 'Previous', data: chartComparison.map((item) => item.previous), color: '#94a3b8', fill: true, pointRadius: 4 }]} themeColor="#94a3b8" tooltipTitle={(index) => chartComparison[index]?.courseName || ''} yMin={0} />
                        </ChartCard>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ minWidth: 160 }}>Period</th>
                            {selectedComparison.map((item) => (
                              <th key={item.key} className="text-center" style={{ minWidth: 140 }}>{item.courseName}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonRows.map((row) => (
                            <tr key={row.period}>
                              <td><div className="fw-semibold">{row.period}</div><div className="small text-muted">{row.label}</div></td>
                              {selectedComparison.map((item) => (
                                <td key={`${row.period}-${item.key}`} className="text-center">
                                  <span className={`badge ${row.period === 'Present' ? 'text-bg-primary' : 'text-bg-light border text-dark'}`}>{row.values[item.key] || 0}</span>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : <EmptyChart label="course comparison" themeColor="#3b82f6" />}
              </div>
            </ChartCard>
          </div>
        )}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><AnimatedKpiCard icon="bi-collection-fill" label="Total Batches" value={kpis.total} loading={loading} color="#3b82f6" sub="selected range" /></div>
        <div className="col-6 col-md-3"><AnimatedKpiCard icon="bi-play-circle-fill" label="Active Batches" value={kpis.active} loading={loading} color="#22c55e" sub="currently running" /></div>
        <div className="col-6 col-md-3"><AnimatedKpiCard icon="bi-check2-all" label="Completed Batches" value={kpis.completed} loading={loading} color="#8b5cf6" sub="finished batches" /></div>
        <div className="col-6 col-md-3"><AnimatedKpiCard icon="bi-hourglass-split" label="Upcoming Batches" value={kpis.upcoming} loading={loading} color="#f59e0b" sub="not yet started" /></div>
      </div>

      <div className="row g-3 mb-4">
        {visible.strength !== false && <div className="col-lg-7"><ChartCard title="Batch Strength Comparison" subtitle={meta.periodLabel || 'Top batches by student count'} icon="bi-bar-chart-fill" iconColor="#3b82f6" onClick={() => navigate('/batches')}>{strength.length ? <BarChart height={Math.max(220, strength.length * 32)} labels={strength.map((b) => b.batchNumber)} datasets={[{ label: 'Students', data: strength.map((b) => b.students), color: '#3b82f6' }]} themeColor="#3b82f6" onBarClick={({ index }) => { const batch = strength[index]; if (batch) navigate(`/batches?selected=${encodeURIComponent(batch.batchId)}`); }} /> : <EmptyChart label="batch strength" themeColor="#3b82f6" />}</ChartCard></div>}
        {visible.status !== false && <div className="col-lg-5"><ChartCard title="Batch Status Distribution" subtitle="Active, completed, upcoming, cancelled" icon="bi-pie-chart-fill" iconColor="#22c55e" onClick={() => navigate('/batches')}>{statusSlices.length ? <DoughnutChart height={280} slices={statusSlices} centerLabel={kpis.total || 0} centerSubLabel="Batches" themeColor="#22c55e" /> : <EmptyChart label="status" themeColor="#22c55e" />}</ChartCard></div>}
      </div>

      <div className="row g-3 mb-4">
        {visible.attendance !== false && <div className="col-lg-8"><ChartCard title="Batch Performance" subtitle={meta.periodLabel || 'Attendance percentage'} icon="bi-graph-up" iconColor="#22c55e" onClick={() => navigate('/attendance')}>{attendance.length ? <LineChart height={280} labels={attendance.map((m) => m.label)} datasets={[{ label: 'Attendance %', data: attendance.map((m) => m.pct), color: '#22c55e', fill: true, pointRadius: 3 }]} yMin={0} yMax={100} yStepSize={25} /> : <EmptyChart label="attendance" themeColor="#22c55e" />}</ChartCard></div>}
        {visible.metrics !== false && <div className="col-lg-4"><ChartCard title="Additional Metrics" subtitle="Student capacity and revenue" icon="bi-bar-chart-line-fill" iconColor="#0ea5e9"><div className="d-flex flex-column gap-3 mt-2"><div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(29,78,216,0.06) 100%)', border: '1px solid rgba(59,130,246,0.25)' }}><div><div style={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Students Enrolled</div><div style={{ fontSize: 22, fontWeight: 800, color: '#1e3a8a' }}>{kpis.students || 0}</div></div><i className="bi bi-people-fill" style={{ fontSize: 32, color: '#3b82f6', opacity: 0.6 }}></i></div><div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(5,150,105,0.06) 100%)', border: '1px solid rgba(16,185,129,0.25)' }}><div><div style={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Revenue</div><div style={{ fontSize: 22, fontWeight: 800, color: '#047857' }}>{fmtINR(kpis.revenue || 0)}</div></div><i className="bi bi-currency-rupee" style={{ fontSize: 32, color: '#10b981', opacity: 0.6 }}></i></div></div></ChartCard></div>}
      </div>
      <AnalyticsSettingsModal show={showSettings} charts={chartOptions} visibility={visible} onToggle={controls.toggleChart} onReset={controls.reset} onClose={() => setShowSettings(false)} />
      <AnalyticsInfoModal show={showInfo} title="Batch Analytics Guide" lines={['Attendance trend now follows the selected date range.', 'Use weekly grouping for operational review and monthly or yearly for management reports.', 'Metric cards remain unchanged outside the chosen analytics filter.']} onClose={() => setShowInfo(false)} />
    </div>
  );
}
